import { useState, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Download, Loader2, AlertCircle, ArrowRight, User, Building2, ChevronDown, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneNumber } from "@/lib/utils";

interface ParsedOfficer {
  firstName: string;
  lastName: string;
  email: string | null;
  fullName: string;
}

interface DocketInfo {
  prefix: string;
  number: string;
  statusCode: string | null;
}

interface RawDOTProfile {
  [key: string]: string | undefined;
}

interface DOTCompanyData {
  name: string;
  dbaName: string | null;
  phone: string | null;
  cellPhone: string | null;
  email: string | null;
  address: string | null;
  mailingAddress: string | null;
  dotNumber: string;
  statusCode: string | null;
  carrierOperation: string | null;
  classDefinition: string | null;
  powerUnits: string | null;
  truckUnits: string | null;
  busUnits: string | null;
  totalDrivers: string | null;
  totalCdl: string | null;
  mcs150Date: string | null;
  addDate: string | null;
  fleetSize: string | null;
  hmIndicator: string | null;
  officers: ParsedOfficer[];
  dockets: DocketInfo[];
  rawProfile: RawDOTProfile;
}

interface FieldSelection {
  name: boolean;
  dbaName: boolean;
  phone: boolean;
  email: boolean;
  address: boolean;
}

interface OfficerSelection {
  [index: number]: {
    include: boolean;
    includeEmail: boolean;
  };
}

// DOT profile fields that get stored on the company
interface DOTProfileFields {
  carrierOperation?: string | null;
  classDefinition?: string | null;
  carship?: string | null;
  powerUnits?: string | null;
  truckUnits?: string | null;
  busUnits?: string | null;
  totalDrivers?: string | null;
  totalCdl?: string | null;
  driverInterTotal?: string | null;
  fleetSize?: string | null;
  hmIndicator?: string | null;
  mcs150Date?: string | null;
  mcs150Mileage?: string | null;
  mcs150MileageYear?: string | null;
  addDate?: string | null;
  interstateBeyond100Miles?: string | null;
  interstateWithin100Miles?: string | null;
  intrastateBeyond100Miles?: string | null;
  intrastateWithin100Miles?: string | null;
  businessOrgDescription?: string | null;
  cargoGenFreight?: string | null;
  cargoLogPole?: string | null;
}

interface DOTCompanyFetchModalProps {
  open: boolean;
  onClose: () => void;
  dotNumber: string;
  currentData?: {
    name?: string;
    dbaName?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  mode: 'create' | 'edit';
  onApply: (data: {
    companyFields: Partial<{ name: string; dbaName: string; phone: string; email: string; address: string; dotNumber: string; mailingAddress: string }> & DOTProfileFields;
    officersToCreate: Array<{ firstName: string; lastName: string; email: string | null }>;
    rawResponse?: any;
  }) => void;
}

export default function DOTCompanyFetchModal({
  open,
  onClose,
  dotNumber: initialDotNumber,
  currentData,
  mode,
  onApply
}: DOTCompanyFetchModalProps) {
  const { toast } = useToast();
  const [dotNumber, setDotNumber] = useState(initialDotNumber);
  const [dotData, setDotData] = useState<DOTCompanyData | null>(null);
  const [fieldSelection, setFieldSelection] = useState<FieldSelection>({
    name: true,
    dbaName: true,
    phone: true,
    email: true,
    address: true
  });
  const [officerSelection, setOfficerSelection] = useState<OfficerSelection>({});
  const [showRawProfile, setShowRawProfile] = useState(false);
  const [fetchRawResponse, setFetchRawResponse] = useState<any>(null);
  const hasAutoFetched = useRef(false);
  const lastInitialDotNumber = useRef(initialDotNumber);

  // Sync dotNumber state when initialDotNumber prop changes
  useEffect(() => {
    if (initialDotNumber !== lastInitialDotNumber.current) {
      setDotNumber(initialDotNumber);
      setDotData(null);
      hasAutoFetched.current = false;
      lastInitialDotNumber.current = initialDotNumber;
    }
  }, [initialDotNumber]);

  const fetchMutation = useMutation({
    mutationFn: async () => {
      if (!dotNumber) {
        throw new Error("DOT number is required");
      }

      const response = await apiRequest("POST", "/api/dot/fetch-company", {
        dotNumber: dotNumber.trim(),
      });

      const data = await response.json();
      const { rawResponse, ...companyData } = data;
      return { companyData: companyData as DOTCompanyData, rawResponse };
    },
    onSuccess: (result) => {
      console.log('[DOT Fetch] Success! Received data:', result.companyData);
      setDotData(result.companyData);
      setFetchRawResponse(result.rawResponse);
      
      const initialOfficerSelection: OfficerSelection = {};
      result.companyData.officers.forEach((_, index) => {
        initialOfficerSelection[index] = {
          include: true,
          includeEmail: index === 0
        };
      });
      setOfficerSelection(initialOfficerSelection);

      toast({
        title: "Success",
        description: `Found company: ${result.companyData.name}`,
      });
    },
    onError: (error: any) => {
      console.error('[DOT Fetch] Error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch company data from DOT",
        variant: "destructive",
      });
    },
  });

  const handleFetch = () => {
    fetchMutation.mutate();
  };

  // Auto-fetch when modal opens with a valid DOT number (skip the redundant second click)
  useEffect(() => {
    if (open && initialDotNumber && initialDotNumber.trim() && !hasAutoFetched.current && !dotData) {
      hasAutoFetched.current = true;
      // Trigger auto-fetch immediately
      fetchMutation.mutate();
    }
    // Reset state when modal closes
    if (!open) {
      hasAutoFetched.current = false;
      setDotData(null);
      setFetchRawResponse(null);
    }
  }, [open, initialDotNumber]);

  const handleApply = () => {
    if (!dotData) return;

    const companyFields: Partial<{ name: string; dbaName: string; phone: string; email: string; address: string; dotNumber: string; mailingAddress: string }> & DOTProfileFields = {
      dotNumber: dotData.dotNumber
    };

    if (fieldSelection.name && dotData.name) {
      companyFields.name = dotData.name;
    }
    if (fieldSelection.dbaName && dotData.dbaName) {
      companyFields.dbaName = dotData.dbaName;
    }
    if (fieldSelection.phone && dotData.phone) {
      companyFields.phone = dotData.phone;
    }
    if (fieldSelection.email && dotData.email) {
      companyFields.email = dotData.email;
    }
    if (fieldSelection.address && dotData.address) {
      companyFields.address = dotData.address;
    }
    
    // Include all DOT profile fields from the fetched data
    if (dotData.carrierOperation) companyFields.carrierOperation = dotData.carrierOperation;
    if (dotData.classDefinition) companyFields.classDefinition = dotData.classDefinition;
    if (dotData.powerUnits) companyFields.powerUnits = dotData.powerUnits;
    if (dotData.truckUnits) companyFields.truckUnits = dotData.truckUnits;
    if (dotData.busUnits) companyFields.busUnits = dotData.busUnits;
    if (dotData.totalDrivers) companyFields.totalDrivers = dotData.totalDrivers;
    if (dotData.totalCdl) companyFields.totalCdl = dotData.totalCdl;
    if (dotData.fleetSize) companyFields.fleetSize = dotData.fleetSize;
    if (dotData.hmIndicator) companyFields.hmIndicator = dotData.hmIndicator;
    if (dotData.mcs150Date) companyFields.mcs150Date = dotData.mcs150Date;
    if (dotData.addDate) companyFields.addDate = dotData.addDate;
    
    // Extract additional fields from rawProfile if available
    const raw = dotData.rawProfile;
    if (raw) {
      if (raw.phy_mailing_addr) {
        // Combine mailing address parts
        const mailingParts = [raw.phy_mailing_addr, raw.phy_m_city, raw.phy_m_st, raw.phy_m_zip].filter(Boolean);
        if (mailingParts.length > 0) {
          companyFields.mailingAddress = mailingParts.join(', ');
        }
      }
      if (raw.carship) companyFields.carship = raw.carship;
      if (raw.driver_inter_total) companyFields.driverInterTotal = raw.driver_inter_total;
      if (raw.mcs150_mileage) companyFields.mcs150Mileage = raw.mcs150_mileage;
      if (raw.mcs150_mileage_year) companyFields.mcs150MileageYear = raw.mcs150_mileage_year;
      if (raw.interstate_beyond_100_miles) companyFields.interstateBeyond100Miles = raw.interstate_beyond_100_miles;
      if (raw.interstate_within_100_miles) companyFields.interstateWithin100Miles = raw.interstate_within_100_miles;
      if (raw.intrastate_beyond_100_miles) companyFields.intrastateBeyond100Miles = raw.intrastate_beyond_100_miles;
      if (raw.intrastate_within_100_miles) companyFields.intrastateWithin100Miles = raw.intrastate_within_100_miles;
      if (raw.business_org_description) companyFields.businessOrgDescription = raw.business_org_description;
      if (raw.cargo_gen_freight) companyFields.cargoGenFreight = raw.cargo_gen_freight;
      if (raw.cargo_log_pole) companyFields.cargoLogPole = raw.cargo_log_pole;
    }

    const officersToCreate: Array<{ firstName: string; lastName: string; email: string | null }> = [];
    dotData.officers.forEach((officer, index) => {
      const selection = officerSelection[index];
      if (selection?.include) {
        officersToCreate.push({
          firstName: officer.firstName,
          lastName: officer.lastName,
          email: selection.includeEmail ? officer.email : null
        });
      }
    });

    onApply({ companyFields, officersToCreate, rawResponse: fetchRawResponse });
    onClose();
  };

  const toggleField = (field: keyof FieldSelection) => {
    setFieldSelection(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const toggleOfficer = (index: number) => {
    setOfficerSelection(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        include: !prev[index]?.include
      }
    }));
  };

  const toggleOfficerEmail = (index: number) => {
    setOfficerSelection(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        includeEmail: !prev[index]?.includeEmail
      }
    }));
  };

  const hasChanges = (field: keyof FieldSelection): boolean => {
    if (!dotData || !currentData) return false;
    const dotValue = dotData[field];
    const currentValue = currentData[field];
    return dotValue !== currentValue && dotValue !== null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Fetch Company from DOT
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="dot-number-input">DOT Number</Label>
              <Input
                id="dot-number-input"
                value={dotNumber}
                onChange={(e) => setDotNumber(e.target.value)}
                placeholder="Enter DOT number"
                data-testid="input-dot-number-fetch"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleFetch}
                disabled={!dotNumber || fetchMutation.isPending}
                data-testid="button-fetch-dot-company"
              >
                {fetchMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Fetch
                  </>
                )}
              </Button>
            </div>
          </div>

          {dotData && (
            <>
              <Separator />
              
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Information
                </h3>
                <p className="text-sm text-gray-500">
                  Select which fields to {mode === 'create' ? 'use' : 'update'}:
                </p>
                
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <FieldRow
                      label="Company Name"
                      dotValue={dotData.name}
                      currentValue={currentData?.name}
                      selected={fieldSelection.name}
                      onChange={() => toggleField('name')}
                      showComparison={mode === 'edit'}
                    />
                    
                    <FieldRow
                      label="DBA Name"
                      dotValue={dotData.dbaName}
                      currentValue={currentData?.dbaName}
                      selected={fieldSelection.dbaName}
                      onChange={() => toggleField('dbaName')}
                      showComparison={mode === 'edit'}
                    />
                    
                    <FieldRow
                      label="Phone"
                      dotValue={formatPhoneNumber(dotData.phone)}
                      currentValue={formatPhoneNumber(currentData?.phone)}
                      selected={fieldSelection.phone}
                      onChange={() => toggleField('phone')}
                      showComparison={mode === 'edit'}
                    />
                    
                    <FieldRow
                      label="Email"
                      dotValue={dotData.email}
                      currentValue={currentData?.email}
                      selected={fieldSelection.email}
                      onChange={() => toggleField('email')}
                      showComparison={mode === 'edit'}
                    />
                    
                    <FieldRow
                      label="Address"
                      dotValue={dotData.address}
                      currentValue={currentData?.address}
                      selected={fieldSelection.address}
                      onChange={() => toggleField('address')}
                      showComparison={mode === 'edit'}
                    />
                  </CardContent>
                </Card>

                {dotData.officers.length > 0 && (
                  <>
                    <h3 className="font-medium flex items-center gap-2 pt-2">
                      <User className="w-4 h-4" />
                      Company Officers ({dotData.officers.length})
                    </h3>
                    <p className="text-sm text-gray-500">
                      Select officers to create as contacts. Email will only be set for the first officer.
                    </p>
                    
                    <div className="space-y-2">
                      {dotData.officers.map((officer, index) => {
                        const selection = officerSelection[index] || { include: true, includeEmail: index === 0 };
                        return (
                          <Card key={index} className={selection.include ? "border-primary/50" : "opacity-60"}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <Checkbox
                                  id={`officer-${index}`}
                                  checked={selection.include}
                                  onCheckedChange={() => toggleOfficer(index)}
                                  data-testid={`checkbox-officer-${index}`}
                                />
                                <div className="flex-1">
                                  <Label htmlFor={`officer-${index}`} className="font-medium cursor-pointer">
                                    {officer.fullName}
                                  </Label>
                                  <div className="text-sm text-gray-500 mt-1">
                                    <p>First: {officer.firstName || '(empty)'}</p>
                                    <p>Last: {officer.lastName || '(empty)'}</p>
                                  </div>
                                  
                                  {officer.email && selection.include && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <Checkbox
                                        id={`officer-email-${index}`}
                                        checked={selection.includeEmail}
                                        onCheckedChange={() => toggleOfficerEmail(index)}
                                        data-testid={`checkbox-officer-email-${index}`}
                                      />
                                      <Label htmlFor={`officer-email-${index}`} className="text-sm cursor-pointer">
                                        Include email: {officer.email}
                                      </Label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </>
                )}

                {dotData.dockets.length > 0 && (
                  <>
                    <h3 className="font-medium pt-2">Docket Information (Reference Only)</h3>
                    <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
                      {dotData.dockets.map((docket, index) => (
                        <p key={index} className="flex items-center gap-2">
                          <span className="font-medium">{docket.prefix}-{docket.number}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            docket.statusCode === 'A' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {docket.statusCode === 'A' ? 'Active' : docket.statusCode === 'I' ? 'Inactive' : docket.statusCode || 'Unknown'}
                          </span>
                        </p>
                      ))}
                    </div>
                  </>
                )}

                {dotData.classDefinition && (
                  <div className="bg-blue-50 p-3 rounded text-sm">
                    <span className="font-medium">Classification:</span> {dotData.classDefinition}
                  </div>
                )}

                <Collapsible open={showRawProfile} onOpenChange={setShowRawProfile}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-2 h-auto" data-testid="button-toggle-raw-profile">
                      <span className="flex items-center gap-2 font-medium">
                        <FileText className="w-4 h-4" />
                        Full DOT Profile Data
                      </span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showRawProfile ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Card className="mt-2">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {Object.entries(dotData.rawProfile)
                            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key, value]) => (
                              <div key={key} className="flex flex-col">
                                <span className="text-gray-500 text-xs font-medium uppercase">{formatFieldName(key)}</span>
                                <span className="text-gray-900 truncate" title={String(value)}>{String(value)}</span>
                              </div>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <Separator />

              <div className="flex justify-between items-center pt-2">
                <div className="text-sm text-gray-600">
                  {Object.values(fieldSelection).filter(Boolean).length} field(s) selected
                  {dotData.officers.length > 0 && (
                    <>, {Object.values(officerSelection).filter(s => s.include).length} officer(s) to create</>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApply}
                    data-testid="button-apply-dot-data"
                  >
                    Apply Selected Data
                  </Button>
                </div>
              </div>
            </>
          )}

          {!dotData && !fetchMutation.isPending && (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Enter a DOT number and click Fetch to retrieve company information</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  label,
  dotValue,
  currentValue,
  selected,
  onChange,
  showComparison
}: {
  label: string;
  dotValue: string | null;
  currentValue?: string;
  selected: boolean;
  onChange: () => void;
  showComparison: boolean;
}) {
  const hasValue = dotValue !== null && dotValue !== '';
  const isDifferent = showComparison && currentValue !== dotValue;
  
  return (
    <div className={`flex items-start gap-3 p-2 rounded ${selected && hasValue ? 'bg-green-50' : ''}`}>
      <Checkbox
        checked={selected && hasValue}
        onCheckedChange={onChange}
        disabled={!hasValue}
        data-testid={`checkbox-field-${label.toLowerCase().replace(/\s+/g, '-')}`}
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        {showComparison && isDifferent ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 line-through truncate max-w-[150px]">
              {currentValue || '(empty)'}
            </span>
            <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-green-700 truncate max-w-[150px]">
              {dotValue || '(empty)'}
            </span>
          </div>
        ) : (
          <div className="text-sm text-gray-600 truncate">
            {dotValue || <span className="text-gray-400">(not available)</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
}
